/**
 * AdminDocs — Documentação dinâmica do GitHub
 * Puxa .md files via /admin/github/tree + /admin/github/file
 */

import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../api/client';

import { T } from '../styles/tokens';
import { ListSkeleton, SkeletonBar } from '../components/ui';
const FONT = "'Cascadia Code','Fira Code','SF Mono',Consolas,monospace";

interface DocEntry {
  path: string;
  name: string;
  folder: string;
  size: number;
}

function renderMarkdown(md: string): string {
  return md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre style="background:#0a0a0a;border:1px solid #333;padding:12px;border-radius:3px;overflow-x:auto;font-size:12px;margin:8px 0;line-height:1.5"><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, `<code style="background:${T.bg};padding:1px 4px;border-radius:3px;font-size:12px">$1</code>`)
    .replace(/^### (.+)$/gm, '<div style="font-size:14px;font-weight:600;color:#D4D4D4;margin:16px 0 6px">$1</div>')
    .replace(/^## (.+)$/gm, '<div style="font-size:16px;font-weight:600;color:#D4D4D4;margin:20px 0 8px;padding-bottom:6px;border-bottom:1px solid ${T.textPrimary}">$1</div>')
    .replace(/^# (.+)$/gm, '<div style="font-size:20px;font-weight:700;color:#D4D4D4;margin:0 0 12px">$1</div>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a href="$2" target="_blank" style="color:${T.primary}">$1</a>`)
    .replace(/^- (.+)$/gm, '<div style="margin-left:16px;padding:2px 0">• $1</div>')
    .replace(/^\d+\. (.+)$/gm, '<div style="margin-left:16px;padding:2px 0">$1</div>')
    .replace(/\n\n/g, '<div style="height:8px"></div>')
    .replace(/\n/g, '<br>');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function AdminDocsPage() {
  const [docTree, setDocTree] = useState<DocEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [contentLoading, setContentLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const loadDoc = useCallback(async (path: string) => {
    setSelectedFile(path);
    setContentLoading(true);
    try {
      const data = await api.get<any>(`/admin/github/file?path=${encodeURIComponent(path)}`);
      setContent(data.content || '');
    } catch {
      setContent('Erro ao carregar documento');
    }
    setContentLoading(false);
  }, []);

  useEffect(() => {
    api.get<any>('/admin/github/tree').then((r: any) => {
      const tree = r.tree || [];
      const docs: DocEntry[] = tree
        .filter((f: any) => f.type === 'file' && f.path.endsWith('.md'))
        .filter((f: any) =>
          f.path.startsWith('docs/') ||
          f.path.startsWith('admin/docs/') ||
          f.path.startsWith('apps/api/docs/') ||
          f.path.startsWith('apps/web/src/pages/docs/') ||
          f.path.startsWith('apps/web/public/admin/docs/') ||
          f.path.startsWith('apps/web/public/docs/')
        )
        .map((f: any) => ({
          path: f.path,
          name: f.path.split('/').pop() || f.path,
          folder: f.path.split('/').slice(0, -1).join('/') || 'root',
          size: f.size || 0,
        }))
        .sort((a: DocEntry, b: DocEntry) => a.path.localeCompare(b.path));

      setDocTree(docs);
      setLoading(false);

      const readme = docs.find((d) => d.path === 'docs/README.md');
      if (readme) loadDoc(readme.path);
      else if (docs.length > 0) loadDoc(docs[0]!.path);
    }).catch(() => setLoading(false));
  }, [loadDoc]);

  // Build folder structure
  const folders = new Map<string, DocEntry[]>();
  const filtered = search
    ? docTree.filter((d) => d.name.toLowerCase().includes(search.toLowerCase()) || d.path.toLowerCase().includes(search.toLowerCase()))
    : docTree;

  for (const doc of filtered) {
    const list = folders.get(doc.folder) || [];
    list.push(doc);
    folders.set(doc.folder, list);
  }

  // Separate root docs/ files from subfolders
  const rootFolder = 'docs';
  const rootFiles = folders.get(rootFolder) || [];
  const subFolders = Array.from(folders.entries())
    .filter(([k]) => k !== rootFolder)
    .sort(([a], [b]) => a.localeCompare(b));

  const toggleFolder = (f: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  };

  const selectedDoc = docTree.find((d) => d.path === selectedFile);

  if (loading) {
    return (
      <div style={{ background: T.bg, padding: 40 }}>
        <ListSkeleton rows={3} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: '80vh', fontFamily: FONT, color: T.textSecondary, background: T.bg }}>
      {/* SIDEBAR */}
      <div style={{ width: 260, minWidth: 260, background: T.bgCard, borderRight: '1px solid #3c3c3c', overflowY: 'auto', padding: '8px 0' }}>
        {/* Search */}
        <div style={{ padding: '4px 8px 8px' }}>
          <input
            type="text"
            placeholder="Buscar docs..."
            value={search}
            onInput={(e: any) => setSearch(e.target.value)}
            style={{
              width: '100%', background: T.bg, border: '1px solid #3c3c3c', color: T.textSecondary,
              padding: '4px 8px', fontSize: 11, fontFamily: FONT, borderRadius: 2, outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Subfolders */}
        {subFolders.map(([folder, files]) => (
          <div key={folder}>
            <div
              onClick={() => toggleFolder(folder)}
              style={{
                padding: '3px 8px', fontSize: 12, cursor: 'pointer', color: T.textSecondary,
                userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <span style={{ fontSize: 10, width: 12, display: 'inline-block' }}>{expandedFolders.has(folder) ? '▼' : ''}</span>
              <span>{folder}</span>
            </div>
            {expandedFolders.has(folder) && files.map((doc) => (
              <div
                key={doc.path}
                onClick={() => loadDoc(doc.path)}
                style={{
                  padding: '2px 8px 2px 28px', fontSize: 11, cursor: 'pointer', color: T.textMuted,
                  background: selectedFile === doc.path ? T.border : 'transparent',
                  borderLeft: selectedFile === doc.path ? `2px solid ${T.primary}` : '2px solid transparent',
                }}
              >
                {doc.name}
              </div>
            ))}
          </div>
        ))}

        {/* Root docs files */}
        {rootFiles.length > 0 && (
          <div>
            <div style={{ borderTop: '1px solid #3c3c3c', margin: '6px 0' }} />
            {rootFiles.map((doc) => (
              <div
                key={doc.path}
                onClick={() => loadDoc(doc.path)}
                style={{
                  padding: '2px 8px 2px 16px', fontSize: 11, cursor: 'pointer', color: T.textMuted,
                  background: selectedFile === doc.path ? T.border : 'transparent',
                  borderLeft: selectedFile === doc.path ? `2px solid ${T.primary}` : '2px solid transparent',
                }}
              >
                {doc.name}
              </div>
            ))}
          </div>
        )}

        <div style={{ padding: '4px 12px', fontSize: 10, color: T.textMuted, marginTop: 8 }}>
          {docTree.length} docs
        </div>
      </div>

      {/* CONTENT */}
      <div style={{ flex: 1, overflowY: 'auto', background: T.bg }}>
        {selectedDoc && (
          <div style={{ padding: '8px 24px 4px', fontSize: 11, color: T.textMuted, borderBottom: '1px solid #333' }}>
            {selectedDoc.path} · {formatSize(selectedDoc.size)}
          </div>
        )}
        <div style={{ padding: 24 }}>
          {contentLoading ? (
            <ListSkeleton rows={4} />
          ) : content ? (
            <div
              style={{ fontSize: 13, lineHeight: 1.6, color: T.textSecondary }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
            />
          ) : (
            <div style={{ color: T.textMuted, fontSize: 12 }}>Selecione um documento na sidebar</div>
          )}
        </div>
      </div>
    </div>
  );
}
